import { useTranslation } from 'react-i18next';

import { Box, Chip } from '@mui/material';
import { SCUIButton, SCUIDateRanger, SCUINoRecordComponent } from '../../../../atomic/atoms';
import { SCUICustomDataGrid } from '../../../../atomic/molecules';

import airdropShipment from '../../../../assets/icons/svg/pageIcons/airdropShipment.svg';
import Helper from '../../../../utils/helper';
import TimeHelper from '../../../../utils/time';

import { AxDurum } from '../enum/AxDurumEnums';
import { NS, nsCls } from '../netSales.classes';

interface NetSalesListProps {
    netSalesList: any[];
    startDate: string | Date;
    endDate: string | Date;
    setStartDate: (date: string | Date) => void;
    setEndDate: (date: string | Date) => void;
    handleSearch: () => void;
    isSearched: boolean;
}

export const NetSalesList = ({
    netSalesList,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    handleSearch,
    isSearched,
}: NetSalesListProps) => {
    const { t } = useTranslation();

    const getStatusTag = (rowStatus?: number | null) => {
        switch (rowStatus) {
            case AxDurum.Calculating:
                return (
                    <Chip
                        className={nsCls(NS.statusChip, NS.statusChipPendingAccounting)}
                        sx={{
                            backgroundColor: '#fff3cd !important',
                            '& .MuiChip-label': {
                                color: '#c1ad61',
                                fontWeight: 'bold',
                            },
                        }}
                        label={t('summaryAggregation.netSales.calculating')}
                    />
                );
            case AxDurum.CalculationCompleted:
                return (
                    <Chip
                        className={nsCls(NS.statusChip, NS.statusChipAccountingComplete)}
                        sx={{
                            backgroundColor: '#22aeee !important',
                            '& .MuiChip-label': {
                                color: '#004a88',
                                fontWeight: 'bold',
                            },
                        }}
                        label={t('summaryAggregation.netSales.calculationCompleted')}
                    />
                );
            case AxDurum.SentToAxapta:
                return (
                    <Chip
                        className={nsCls(NS.statusChip, NS.statusChipTransferComplete)}
                        label={t('summaryAggregation.netSales.sentToAxapta')}
                        sx={{
                            backgroundColor: '#cdead8 !important',
                            '& .MuiChip-label': {
                                color: '#50755f',
                                fontWeight: 'bold',
                            },
                        }}
                    />
                );
            default:
                return <>-</>;
        }
    };

    const formatAmount = (value: number | null | undefined) => {
        if (value === null || value === undefined || Number.isNaN(Number(value))) {
            return '-';
        }
        const countryCode = Helper.getCountryCodeFromCountryId() || 'TR';
        return Helper.formatCurrencyByCountry(Number(value), countryCode);
    };

    const columns = [
        {
            field: 'consolidationDate',
            headerName: t('summaryAggregation.netSales.columns.icmalTarihi'),
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            renderCell: (values: any) => {
                return values?.row?.consolidationDate
                    ? TimeHelper.formatDate(values.row.consolidationDate)
                    : '-';
            },
        },
        {
            field: 'lastConsolidationReceivedDate',
            headerName: t('summaryAggregation.netSales.columns.sonIcmalAlinmaTarihi'),
            flex: 1.2,
            align: 'center',
            headerAlign: 'center',
            renderCell: (values: any) => {
                return values?.row?.lastConsolidationReceivedDate
                    ? TimeHelper.formatDateHourSecond(values.row.lastConsolidationReceivedDate)
                    : '-';
            },
        },
        {
            field: 'netSales',
            headerName: t('summaryAggregation.netSales.columns.netSatislar'),
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            renderCell: (values: any) => formatAmount(values?.row?.netSales),
        },
        {
            field: 'axStatus',
            headerName: t('summaryAggregation.netSales.columns.axDurum'),
            flex: 1.2,
            align: 'center',
            headerAlign: 'center',
            renderCell: (values: any) => getStatusTag(values?.row?.axStatus),
        },
    ];

    const handleChangeDate = (event: any) => {
        setStartDate(event[0]);
        setEndDate(event[1]);
    };

    return (
        <Box className={NS.list}>
            <Box
                className={NS.listSearchBar}
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    margin: '24px 0 32px 0',
                    '& .sc-btn__text': {
                        padding: '8px 16px !important',
                    },
                }}
            >
                <p
                    className={NS.listSearchLabel}
                    style={{ fontSize: '14px', fontWeight: '600', marginRight: '12px' }}
                >
                    {t('summaryAggregation.netSales.dateRange')}:
                </p>
                <Box className={nsCls(NS.listDateRanger, NS.dateRange)}>
                    <SCUIDateRanger
                        fromDate={startDate}
                        toDate={endDate}
                        setDateRange={handleChangeDate}
                        dateFormat="YYYY-MM-DD"
                    />
                </Box>
                <SCUIButton
                    className={NS.btnSearch}
                    style={{ marginLeft: '24px' }}
                    text={t('general.search')}
                    type="primary"
                    sizeType="fixed"
                    size={'m'}
                    disabled={!startDate || !endDate}
                    onClick={handleSearch}
                />
            </Box>
            <Box className={NS.listGridContainer}>
                {!isSearched ? (
                    <Box className={NS.listNoRecord}>
                        <SCUINoRecordComponent img={airdropShipment} />
                    </Box>
                ) : (
                    <Box className={NS.listGrid}>
                        <SCUICustomDataGrid
                            getRowClassName={'net-sales-list-grid'}
                            rows={netSalesList || []}
                            columns={columns}
                            rowHeight={75}
                            hideFooter
                            allrecord
                        />
                    </Box>
                )}
            </Box>
        </Box>
    );
};
